import { useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { authFetch } from '../lib/authFetch'
import { useTranslation } from '../hooks/useTranslation'
import { PIPELINE_STAGES } from '../lib/pipelineRegistry'
import { formatEnumLabel, formatStageLabel, formatDomainLabel, getDomainIcon } from '../lib/formatters'
import type { FilterOptionsV2, FilterOptionsResponse, ProjectFiltersV2 } from '../types'

// ─── Props ────────────────────────────────────────────────────────────────

interface FilterSidebarProps {
  filters: ProjectFiltersV2
  onChange: (filters: ProjectFiltersV2) => void
}

// ─── CollapsibleSection ──────────────────────────────────────────────────

interface CollapsibleSectionProps {
  title: string
  children: ReactNode
  defaultOpen?: boolean
  activeCount?: number
}

function CollapsibleSection({ title, children, defaultOpen = true, activeCount = 0 }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="py-2.5 border-b border-border-subtle/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between mb-1.5"
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-text-tertiary uppercase tracking-[0.5px]">
            {title}
          </span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-primary-red text-white rounded-full">
              {activeCount}
            </span>
          )}
        </div>
        <span
          className="text-text-tertiary text-[10px] transition-transform duration-200"
          style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}
        >
          &#9654;
        </span>
      </button>
      {open && <div className="flex flex-col">{children}</div>}
    </div>
  )
}

// ─── CheckboxRow ─────────────────────────────────────────────────────────

interface CheckboxRowProps {
  label: string
  count?: number
  checked: boolean
  onChange: () => void
}

function CheckboxRow({ label, count, checked, onChange }: CheckboxRowProps) {
  return (
    <label className="flex items-center gap-2 py-0.5 px-1 rounded cursor-pointer hover:bg-bg-card-hover text-[12px] text-text-secondary min-h-[28px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-3 h-3 rounded border-border-subtle accent-[#E61B25] flex-shrink-0"
      />
      <span className="flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="text-[10px] text-text-tertiary flex-shrink-0 tabular-nums">{count}</span>
      )}
    </label>
  )
}

// ─── Searchable Section (for owner/institution with many items) ──────────

interface SearchableCheckboxSectionProps {
  title: string
  items: { value: string; label: string; count?: number }[]
  selected: string[]
  onToggle: (value: string) => void
  defaultOpen?: boolean
  maxVisible?: number
}

function SearchableCheckboxSection({
  title,
  items,
  selected,
  onToggle,
  defaultOpen = false,
  maxVisible = 8,
}: SearchableCheckboxSectionProps) {
  const [search, setSearch] = useState('')
  const [showAll, setShowAll] = useState(false)
  const filtered = items.filter(i =>
    i.label.toLowerCase().includes(search.toLowerCase()),
  )
  const visible = search ? filtered : (showAll ? filtered : filtered.slice(0, maxVisible))

  return (
    <CollapsibleSection title={title} activeCount={selected.length} defaultOpen={defaultOpen}>
      <div className="w-full space-y-1">
        {items.length > maxVisible && (
          <input
            type="text"
            placeholder="Search..."
            className="w-full px-3 py-1.5 bg-bg-input border border-border-subtle rounded-md text-body-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-border-strong transition-colors mb-1"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        )}
        {visible.map(item => (
          <CheckboxRow
            key={item.value}
            label={item.label}
            count={item.count}
            checked={selected.includes(item.value)}
            onChange={() => onToggle(item.value)}
          />
        ))}
        {!search && filtered.length > maxVisible && (
          <button
            type="button"
            onClick={() => setShowAll(prev => !prev)}
            className="text-[10px] text-primary-red hover:text-primary-red/80 px-1 py-0.5 cursor-pointer"
          >
            {showAll ? '← Show less' : `+${filtered.length - maxVisible} more`}
          </button>
        )}
      </div>
    </CollapsibleSection>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function toArray(rec: Record<string, { count: number }> | undefined) {
  return Object.entries(rec || {}).map(([key, val]) => ({
    value: key,
    label: key,
    count: Number(val?.count ?? 0),
  }))
}

function toSelectOptions(arr: Array<{ name?: string; id?: number | string }> | undefined) {
  return (arr || [])
    .filter(item => item && (item.name || item.id))
    .map(item => ({
      value: String(item.id ?? item.name ?? ''),
      label: String(item.name ?? item.id ?? ''),
    }))
}

// ─── Build filter query string for filter-options API ────────────────────

function buildFilterQuery(filters: ProjectFiltersV2): string {
  const params = new URLSearchParams()
  if (filters.domain.length > 0) params.set('domain', filters.domain.join(','))
  if (filters.pipeline_status.length > 0) params.set('pipeline_status', filters.pipeline_status.join(','))
  if (filters.owner.length > 0) params.set('owner', filters.owner.join(','))
  if (filters.institution.length > 0) params.set('institution', filters.institution.join(','))
  if (filters.industry.length > 0) params.set('industry', filters.industry.join(','))
  if (filters.source_class.length > 0) params.set('source_class', filters.source_class.join(','))
  if (filters.city.length > 0) params.set('city', filters.city.join(','))
  if (filters.dev_phase.length > 0) params.set('dev_phase', filters.dev_phase.join(','))
  if (filters.therapeutic_area.length > 0) params.set('therapeutic_area', filters.therapeutic_area.join(','))
  if (filters.modality.length > 0) params.set('modality', filters.modality.join(','))
  if (filters.competitive_category.length > 0) params.set('competitive_category', filters.competitive_category.join(','))
  if (filters.q) params.set('q', filters.q)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// ─── FilterSidebar ───────────────────────────────────────────────────────

export function FilterSidebar({ filters, onChange }: FilterSidebarProps) {
  const { t } = useTranslation()
  const [options, setOptions] = useState<FilterOptionsV2>({
    domains: [],
    pipeline_stages: [],
    institutions: [],
    owners: [],
    industries: [],
  })
  const [loading, setLoading] = useState(true)

  // Fetch filter options with current filters for cross-filtering counts
  useEffect(() => {
    const url = `/api/v2/projects/filter-options${buildFilterQuery(filters)}`

    authFetch(url)
      .then(r => r.json())
      .then((raw: FilterOptionsResponse) => {
        const parsed: FilterOptionsV2 = {
          domains: toArray(raw.domains),
          pipeline_stages: toArray(raw.pipeline_stages),
          institutions: toSelectOptions(raw.institutions),
          owners: toSelectOptions(raw.owners),
          industries: toArray(raw.industries),
          source_classes: toArray(raw.source_classes),
          cities: toArray(raw.cities),
          ...(raw.dev_phases ? { dev_phases: toArray(raw.dev_phases) } : {}),
          ...(raw.therapeutic_areas ? { therapeutic_areas: toArray(raw.therapeutic_areas) } : {}),
          ...(raw.modalities ? { modalities: toArray(raw.modalities) } : {}),
          ...(raw.competitive_categories ? { competitive_categories: toArray(raw.competitive_categories) } : {}),
        }
        setOptions(parsed)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [filters])

  // ─── Toggle helper ───────────────────────────────────────────────────

  const toggle = (field: keyof ProjectFiltersV2, value: string) => {
    const current = filters[field]
    if (!Array.isArray(current)) return
    const next = (current as string[]).includes(value)
      ? (current as string[]).filter(v => v !== value)
      : [...(current as string[]), value]
    onChange({ ...filters, [field]: next })
  }

  // ─── Active count ────────────────────────────────────────────────────

  const totalActive =
    filters.domain.length +
    filters.pipeline_status.length +
    filters.owner.length +
    filters.institution.length +
    filters.industry.length +
    filters.source_class.length +
    filters.city.length +
    filters.dev_phase.length +
    filters.therapeutic_area.length +
    filters.modality.length +
    filters.competitive_category.length

  const clearAll = () => {
    onChange({
      domain: [],
      pipeline_status: [],
      score_min: null,
      score_max: null,
      scored: null,
      owner: [],
      institution: [],
      industry: [],
      q: '',
      dev_phase: [],
      therapeutic_area: [],
      modality: [],
      source_class: [],
      city: [],
      competitive_category: [],
    })
  }

  // ─── Domain-conditional visibility ───────────────────────────────────

  const isBiotechSelected = filters.domain.includes('biotech')
  const showDomainSpecific = filters.domain.length > 0

  // ─── Loading state ───────────────────────────────────────────────────

  if (loading) {
    return (
      <aside className="w-filter-panel flex-shrink-0 bg-bg-surface border border-border-subtle rounded-lg p-4">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-4 bg-bg-surface rounded" />
          ))}
        </div>
      </aside>
    )
  }

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <aside className="w-filter-panel flex-shrink-0 bg-bg-surface border border-border-subtle rounded-lg flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-text-secondary">
            Filters
          </span>
          {totalActive > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-primary-red text-white rounded-full">
              {totalActive}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-3 py-1">
        {/* Domain */}
        <CollapsibleSection title={t('filters.domain')} activeCount={filters.domain.length}>
          {options.domains.map(domain => (
            <CheckboxRow
              key={domain.value}
              label={`${getDomainIcon(domain.value)} ${formatDomainLabel(domain.value)}`}
              count={domain.count}
              checked={filters.domain.includes(domain.value)}
              onChange={() => toggle('domain', domain.value)}
            />
          ))}
        </CollapsibleSection>

        {/* Pipeline Stage */}
        <CollapsibleSection title={t('filters.pipeline_stage')} activeCount={filters.pipeline_status.length}>
          {[...options.pipeline_stages].sort((a, b) => {
            const orderA = PIPELINE_STAGES.findIndex(s => s.code === a.value)
            const orderB = PIPELINE_STAGES.findIndex(s => s.code === b.value)
            return orderA - orderB
          }).map(stage => (
            <CheckboxRow
              key={stage.value}
              label={formatStageLabel(stage.value)}
              count={stage.count}
              checked={filters.pipeline_status.includes(stage.value)}
              onChange={() => toggle('pipeline_status', stage.value)}
            />
          ))}
        </CollapsibleSection>

        {/* ── Domain-conditional: Biotech sections ── */}

        {showDomainSpecific && isBiotechSelected && (
          (options.therapeutic_areas?.length || options.modalities?.length || options.dev_phases?.length || options.competitive_categories?.length) ? (
            <div className="border-l-2 border-primary-red/30 pl-2 ml-1">
              {options.therapeutic_areas && options.therapeutic_areas.length > 0 && (
                <CollapsibleSection title={t('filters.therapeutic_area')} activeCount={filters.therapeutic_area.length} defaultOpen={false}>
                  {options.therapeutic_areas.map(area => (
                    <CheckboxRow
                      key={area.value}
                      label={area.label}
                      count={area.count}
                      checked={filters.therapeutic_area.includes(area.value)}
                      onChange={() => toggle('therapeutic_area', area.value)}
                    />
                  ))}
                </CollapsibleSection>
              )}

              {options.modalities && options.modalities.length > 0 && (
                <CollapsibleSection title={t('filters.modality')} activeCount={filters.modality.length} defaultOpen={false}>
                  {options.modalities.map(mod => (
                    <CheckboxRow
                      key={mod.value}
                      label={mod.label}
                      count={mod.count}
                      checked={filters.modality.includes(mod.value)}
                      onChange={() => toggle('modality', mod.value)}
                    />
                  ))}
                </CollapsibleSection>
              )}

              {options.dev_phases && options.dev_phases.length > 0 && (
                <CollapsibleSection title={t('filters.dev_phase')} activeCount={filters.dev_phase.length} defaultOpen={false}>
                  {options.dev_phases.map(phase => (
                    <CheckboxRow
                      key={phase.value}
                      label={phase.label}
                      count={phase.count}
                      checked={filters.dev_phase.includes(phase.value)}
                      onChange={() => toggle('dev_phase', phase.value)}
                    />
                  ))}
                </CollapsibleSection>
              )}

              {options.competitive_categories && options.competitive_categories.length > 0 && (
                <CollapsibleSection title={t('filters.competitive_category')} activeCount={filters.competitive_category.length} defaultOpen={false}>
                  {options.competitive_categories.map(cat => (
                    <CheckboxRow
                      key={cat.value}
                      label={cat.label}
                      count={cat.count}
                      checked={filters.competitive_category.includes(cat.value)}
                      onChange={() => toggle('competitive_category', cat.value)}
                    />
                  ))}
                </CollapsibleSection>
              )}
            </div>
          ) : null
        )}

        {/* ── Global sections ── */}

        {/* Source Class */}
        {options.source_classes && options.source_classes.length > 0 && (
          <CollapsibleSection title={t('filters.source_class')} activeCount={filters.source_class.length} defaultOpen={false}>
            {options.source_classes.map(sc => (
              <CheckboxRow
                key={sc.value}
                label={formatEnumLabel(sc.value)}
                count={sc.count}
                checked={filters.source_class.includes(sc.value)}
                onChange={() => toggle('source_class', sc.value)}
              />
            ))}
          </CollapsibleSection>
        )}

        {/* City */}
        {options.cities && options.cities.length > 0 && (
          <CollapsibleSection title={t('filters.city')} activeCount={filters.city.length} defaultOpen={false}>
            {options.cities.map(city => (
              <CheckboxRow
                key={city.value}
                label={city.label}
                count={city.count}
                checked={filters.city.includes(city.value)}
                onChange={() => toggle('city', city.value)}
              />
            ))}
          </CollapsibleSection>
        )}

        {/* Industry */}
        {options.industries.length > 0 && (
          <SearchableCheckboxSection
            title={t('filters.industry')}
            items={options.industries}
            selected={filters.industry}
            onToggle={v => toggle('industry', v)}
            defaultOpen={false}
          />
        )}

        {/* Owner */}
        {options.owners.length > 0 && (
          <SearchableCheckboxSection
            title={t('filters.owner')}
            items={options.owners}
            selected={filters.owner}
            onToggle={v => toggle('owner', v)}
            defaultOpen={false}
          />
        )}

        {/* Institution */}
        {options.institutions.length > 0 && (
          <SearchableCheckboxSection
            title={t('filters.institution')}
            items={options.institutions}
            selected={filters.institution}
            onToggle={v => toggle('institution', v)}
            defaultOpen={false}
          />
        )}
      </div>

      {/* Clear all (sticky bottom) */}
      {totalActive > 0 && (
        <div className="border-t border-border-subtle px-4 py-3 flex-shrink-0">
          <button
            type="button"
            onClick={clearAll}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border-subtle text-text-secondary text-[12px] font-medium hover:bg-bg-surface-hover hover:text-text-primary transition-colors"
          >
            {t('filters.clear_all', { count: totalActive })}
          </button>
        </div>
      )}
    </aside>
  )
}
